import { TRYON_SERVER_URL } from "../config";

const VIDEO_DURATIONS = [4, 6, 8];
const VIDEO_ASPECTS = ["16:9", "9:16"];
const VIDEO_RESOLUTIONS = ["720p", "1080p", "4k"];
const POLL_INTERVAL = 5000;
const MAX_ATTEMPTS = 120;

const videoCredits = (duration = 8, resolution = "1080p") => {
    const seconds = Number(duration) || 8;
    return seconds * (String(resolution).toLowerCase() === "4k" ? 120 : 80);
};

const normalizeVideoSettings = ({ duration, resolution, aspectRatio }) => {
    const seconds = VIDEO_DURATIONS.includes(Number(duration)) ? Number(duration) : 8;
    let size = VIDEO_RESOLUTIONS.includes(String(resolution || "").toLowerCase())
        ? String(resolution).toLowerCase()
        : "1080p";
    if (seconds !== 8 && size !== "720p") {
        size = "720p";
    }
    const aspect = VIDEO_ASPECTS.includes(aspectRatio) ? aspectRatio : "16:9";
    return { duration: seconds, resolution: size, aspectRatio: aspect };
};

const readError = async (response) => {
    const payload = await response.json().catch(() => null);
    return payload?.message || payload?.error || "Failed to generate the video";
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

class VideoGenerationService {
    constructor(accessToken) {
        this.accessToken = accessToken;
    }

    async generateVideo(settings, onProgress = null) {
        const prompt = String(settings?.prompt || "").trim();
        if (!prompt) {
            return { status: "error", statusCode: 400, message: "Enter a prompt", data: null };
        }

        const normalized = normalizeVideoSettings(settings || {});
        const formData = new FormData();
        formData.append("prompt", prompt);
        formData.append("duration", String(normalized.duration));
        formData.append("resolution", normalized.resolution);
        formData.append("aspect_ratio", normalized.aspectRatio);
        if (settings?.imageFile) {
            formData.append("image", settings.imageFile);
        }

        if (onProgress) {
            onProgress({ stage: "starting", message: "Starting the video", subMessage: "Sending the prompt", progress: 5 });
        }

        const started = await fetch(`${TRYON_SERVER_URL}/api/v1/video/generate/`, {
            method: "POST",
            headers: { Authorization: `Bearer ${this.accessToken}` },
            body: formData,
        });

        if (!started.ok) {
            return {
                status: "error",
                statusCode: started.status,
                message: await readError(started),
                data: null,
            };
        }

        const job = await started.json().catch(() => null);
        const generationId = job?.generation_id;
        if (!generationId) {
            return { status: "error", statusCode: 500, message: "The video job did not start", data: null };
        }

        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
            if (onProgress) {
                onProgress({
                    stage: "processing",
                    message: "Making the video",
                    subMessage: "This can take a few minutes",
                    progress: Math.min(90, 10 + attempt),
                });
            }

            const statusResponse = await fetch(
                `${TRYON_SERVER_URL}/api/v1/video/status/?generation_id=${generationId}`,
                { headers: { Authorization: `Bearer ${this.accessToken}` } }
            );

            if (!statusResponse.ok) {
                return {
                    status: "error",
                    statusCode: statusResponse.status,
                    message: await readError(statusResponse),
                    data: null,
                };
            }

            const statusPayload = await statusResponse.json().catch(() => null);
            if (statusPayload?.status === "failed") {
                return {
                    status: "error",
                    statusCode: 400,
                    message: statusPayload.error || statusPayload.message || "The video failed",
                    data: null,
                };
            }

            if (statusPayload?.status === "completed") {
                const videoUrl = await this.downloadVideo(generationId);
                if (!videoUrl) {
                    return { status: "error", statusCode: 500, message: "The video file did not download", data: null };
                }
                if (onProgress) {
                    onProgress({ stage: "completed", message: "Video ready", subMessage: "", progress: 100 });
                }
                return {
                    status: "success",
                    statusCode: 200,
                    message: "Video ready",
                    data: { videoUrl, jobId: generationId, generationId },
                };
            }

            await sleep(POLL_INTERVAL);
        }

        return {
            status: "error",
            statusCode: 408,
            message: "The video is still running. Check My Gallery in a few minutes.",
            data: null,
        };
    }

    async downloadVideo(generationId) {
        for (let attempt = 0; attempt < 5; attempt += 1) {
            const response = await fetch(
                `${TRYON_SERVER_URL}/api/v1/video/download/?generation_id=${generationId}`,
                { headers: { Authorization: `Bearer ${this.accessToken}` } }
            );
            if (response.status === 409) {
                await sleep(POLL_INTERVAL);
                continue;
            }
            if (!response.ok) {
                return null;
            }
            const blob = await response.blob();
            return URL.createObjectURL(blob);
        }
        return null;
    }
}

const createVideoGenerationService = (accessToken) => new VideoGenerationService(accessToken);

const cleanupVideoBlobUrl = (blobUrl) => {
    if (blobUrl && String(blobUrl).startsWith("blob:")) {
        URL.revokeObjectURL(blobUrl);
    }
};

const calculateVideoCost = (settings = {}) =>
    videoCredits(settings.duration, settings.resolution);

export {
    videoCredits,
    createVideoGenerationService,
    cleanupVideoBlobUrl,
    calculateVideoCost,
    normalizeVideoSettings,
};
